import { useState } from "react";
import { AlertCircle, CalendarCheck, CalendarX, Clock, TrendingUp, UserX, Users, Wallet, CircleDollarSign } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, KpiCard, PageHeader } from "@/components/common/Page";
import { ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useFetch } from "@/hooks/useFetch";
import { addDays, brl, fmtDate, toISO, todayISO } from "@/lib/format";

const now = new Date();
const PRESETS = [
  ["Este mês", toISO(new Date(now.getFullYear(), now.getMonth(), 1)), todayISO()],
  ["Últimos 90 dias", toISO(addDays(now, -90)), todayISO()],
  ["Este ano", `${now.getFullYear()}-01-01`, todayISO()],
];

const SimpleTable = ({ title, cols, rows, testId }) => (
  <div className="overflow-x-auto rounded-xl border bg-card" data-testid={testId}>
    <h3 className="border-b px-4 py-3 font-heading font-semibold">{title}</h3>
    <table className="w-full text-left text-sm">
      <thead><tr className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">{cols.map(([, l]) => <th key={l} className="p-3">{l}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border/50">{cols.map(([k, , fmt]) => <td key={k} className="p-3">{fmt ? fmt(r[k], r) : r[k]}</td>)}</tr>)}</tbody>
    </table>
    {rows.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Sem dados no período.</p>}
  </div>
);

export default function Reports() {
  const [range, setRange] = useState([PRESETS[0][1], PRESETS[0][2]]);
  const { data, loading, error, reload } = useFetch(`/reports?start=${range[0]}&end=${range[1]}`);

  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Relatórios" subtitle="Indicadores do período selecionado" />
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(([l, s, e], i) => (
            <Button key={l} variant={range[0] === s && range[1] === e ? "default" : "outline"} size="sm" onClick={() => setRange([s, e])} data-testid={`report-preset-${i}`}>{l}</Button>
          ))}
        </div>
        <div className="flex gap-3">
          <Field label="De"><Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} data-testid="report-start-input" /></Field>
          <Field label="Até"><Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} data-testid="report-end-input" /></Field>
        </div>
      </div>
      {error ? <ErrorState error={error} onRetry={reload} /> : loading || !data ? <LoadingState rows={4} /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <KpiCard label="Receita no período" value={brl(data.receita_periodo)} icon={Wallet} tone="text-accent" testId="report-revenue" />
            <KpiCard label={`Receita anual (${data.ano})`} value={brl(data.receita_anual)} icon={TrendingUp} testId="report-annual" />
            <KpiCard label="Pagamentos pendentes" value={brl(data.pendentes_total)} icon={AlertCircle} tone="text-amber-500" testId="report-pending" />
            <KpiCard label="Receita média por aluno" value={brl(data.receita_media_aluno)} icon={CircleDollarSign} testId="report-avg" />
            <KpiCard label="Alunos ativos" value={data.alunos_ativos} icon={Users} testId="report-active" />
            <KpiCard label="Aulas realizadas" value={data.aulas_realizadas} icon={CalendarCheck} tone="text-accent" testId="report-done" />
            <KpiCard label="Aulas canceladas" value={data.aulas_canceladas} icon={CalendarX} tone="text-destructive" testId="report-canceled" />
            <KpiCard label="Faltas" value={data.faltas} icon={UserX} tone="text-orange-500" testId="report-absences" />
            <KpiCard label="Horas trabalhadas" value={`${data.horas_trabalhadas} h`} icon={Clock} testId="report-hours" />
          </div>
          <div className="rounded-xl border bg-card p-5" data-testid="report-revenue-chart">
            <h3 className="mb-4 font-heading font-semibold">Receita mensal</h3>
            <div className="h-60">
              <ResponsiveContainer>
                <BarChart data={data.receita_mensal}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} /><YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} width={55} />
                  <Tooltip formatter={(v) => brl(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="valor" name="Receita" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <SimpleTable title="Pagamentos pendentes" testId="report-pending-table" rows={data.pendentes}
            cols={[["student_name", "Aluno"], ["description", "Descrição"], ["due_date", "Vencimento", fmtDate], ["amount", "Valor", brl], ["status", "Status", (v) => <StatusBadge status={v} />]]} />
          {data.advanced ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <SimpleTable title="Por disciplina" testId="report-by-subject" rows={data.por_disciplina} cols={[["subject", "Disciplina"], ["aulas", "Aulas"], ["horas", "Horas"]]} />
              <SimpleTable title="Por aluno" testId="report-by-student" rows={data.por_aluno} cols={[["student_name", "Aluno"], ["aulas", "Aulas"], ["faltas", "Faltas"], ["receita", "Receita", brl]]} />
            </div>
          ) : <p className="text-sm text-muted-foreground" data-testid="report-advanced-locked">Relatórios avançados (por disciplina e por aluno) estão disponíveis no plano Profissional.</p>}
        </div>
      )}
    </div>
  );
}
